import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleMarker, Popup, Tooltip, useMap } from 'react-leaflet';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { getInmetObservations, listInmetStations } from '../services/inmetService';
import { formatInmetTime, formatInmetValue, inmetHistory48h, inmetSummary } from './inmetDomain';
import './InmetStations.css';

const CHART_METRICS = {
  temperature: { label: 'Temperatura', unit: ' °C', color: '#cf6043' },
  humidity: { label: 'Umidade', unit: ' %', color: '#147cbd' },
  rain_mm: { label: 'Chuva horária', unit: ' mm', color: '#007f96' },
  wind_speed: { label: 'Vento', unit: ' m/s', color: '#6772bc' },
};

const MOBILE_QUERY = '(max-width: 720px), (max-width: 980px) and (max-height: 520px)';

function chartTime(value) {
  return new Date(value).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit' });
}

export function InmetStationPanel({ station, distanceKm, compact = false }) {
  const [snapshot, setSnapshot] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [metric, setMetric] = useState('temperature');
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let active = true;
    let busy = false;
    async function load() {
      if (busy) return;
      busy = true;
      setLoading(true);
      setError('');
      setNow(new Date());
      try {
        const data = await getInmetObservations(station.codigo);
        if (!active) return;
        setSnapshot(data);
      } catch (err) {
        console.error('[INMET] Erro ao consultar estação:', err);
        if (active) setError(err.message || 'Não foi possível consultar esta estação.');
      } finally {
        busy = false;
        if (active) setLoading(false);
      }
    }
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, 60000);
    return () => { active = false; clearInterval(timer); };
  }, [station.codigo, reload]);

  const summary = inmetSummary(snapshot, now);
  const history = inmetHistory48h(snapshot, now);
  const latest = summary.latest;
  const chart = CHART_METRICS[metric];
  const metrics = [
    ['Temperatura', formatInmetValue(latest?.temperature, ' °C')],
    ['Umidade', formatInmetValue(latest?.humidity, ' %')],
    ['Chuva na hora', formatInmetValue(latest?.rain_mm, ' mm')],
    ['Vento', formatInmetValue(latest?.wind_speed, ' m/s')],
    ['Rajada', formatInmetValue(latest?.wind_gust, ' m/s')],
    ['Direção do vento', formatInmetValue(latest?.wind_direction, '°')],
  ];

  return (
    <section className="inmet-station-panel" aria-label={`Dados INMET ${station.nome}`}>
      <header>
        <span className="inmet-source-label">INMET · Estação automática</span>
        <h3>{station.nome} / {station.uf}</h3>
        <small>{station.codigo} · Situação cadastral: {station.situacao}</small>
        {Number.isFinite(distanceKm) && <small hidden={compact}>{formatInmetValue(distanceKm, ' km')} do centro da propriedade (linha reta)</small>}
        <small hidden={compact}>{formatInmetValue(station.latitude, '°', 5)}, {formatInmetValue(station.longitude, '°', 5)} · Altitude: {formatInmetValue(station.altitude, ' m')}</small>
      </header>

      {error && <p className="inmet-warning" role="alert">{error}</p>}
      {snapshot?.erro && <p className="inmet-warning" role="status">{snapshot.erro}</p>}
      {latest ? (
        <>
          <div className={`inmet-observation-time${summary.stale ? ' is-stale' : ''}`}>
            <strong>{summary.stale ? 'Medição defasada' : 'Última medição disponível'}</strong>
            <time dateTime={latest.timestamp}>{formatInmetTime(latest.timestamp)}</time>
          </div>
          <dl className="inmet-metrics">
            {(compact ? metrics.slice(0, 3) : metrics).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          </dl>
          <div className="inmet-rain-summary">
            <strong>Chuva nas últimas 24 h: {formatInmetValue(summary.rain24h, ' mm')}</strong>
            <span>{summary.rainComplete ? '24 de 24 horas com leitura' : `Acumulado incompleto: ${summary.rainHours} de 24 horas com leitura`}</span>
          </div>
          <div className="inmet-details" hidden={compact}>
          <label className="inmet-chart-selector">
            Histórico de 48 horas
            <select aria-label="Variável do gráfico INMET" value={metric} onChange={event => setMetric(event.target.value)}>
              {Object.entries(CHART_METRICS).map(([key, item]) => <option value={key} key={key}>{item.label}</option>)}
            </select>
          </label>
          <div className="inmet-chart" aria-label={`Gráfico de ${chart.label.toLowerCase()} nas últimas 48 horas`}>
            <ResponsiveContainer width="100%" height={145}>
              <LineChart data={history} margin={{ top: 8, right: 12, bottom: 0, left: -24 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                <XAxis dataKey="timestamp" tickFormatter={chartTime} minTickGap={45} tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <ChartTooltip labelFormatter={formatInmetTime} formatter={value => [formatInmetValue(value, chart.unit), chart.label]} />
                <Line type="linear" dataKey={metric} stroke={chart.color} strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <details className="inmet-history">
            <summary>Ver tabela das últimas 48 horas</summary>
            <div className="inmet-history-scroll">
              <table>
                <caption>Horários locais; “—” indica ausência de leitura.</caption>
                <thead><tr><th>Horário</th><th>°C</th><th>%</th><th>mm</th><th>m/s</th></tr></thead>
                <tbody>
                  {[...history].reverse().map(reading => (
                    <tr key={reading.timestamp}>
                      <td><time dateTime={reading.timestamp}>{chartTime(reading.timestamp)}</time></td>
                      <td>{formatInmetValue(reading.temperature)}</td><td>{formatInmetValue(reading.humidity)}</td>
                      <td>{formatInmetValue(reading.rain_mm)}</td><td>{formatInmetValue(reading.wind_speed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
          </div>
        </>
      ) : (
        <p role="status">{loading ? 'Consultando medições…' : error ? 'Não foi possível verificar as medições no cache.' : snapshot?.erro
          ? 'Nenhuma medição disponível no cache.'
          : 'Aguardando a primeira coleta agendada desta estação.'}</p>
      )}
      <footer>
        <span>Coleta válida: {formatInmetTime(snapshot?.coletado_em)}</span>
        {snapshot?.tentativa_em && <span>Última tentativa: {formatInmetTime(snapshot.tentativa_em)}</span>}
        {summary.collectionDelayed && <span className="inmet-warning">Coleta atrasada; as medições podem estar desatualizadas.</span>}
        <button type="button" disabled={loading} onClick={() => setReload(value => value + 1)}>
          {loading ? 'Consultando…' : 'Atualizar consulta'}
        </button>
        <small hidden={compact}>Coleta automática em lotes para todas as estações. Atualizar consulta apenas relê o cache. O horário da coleta não é o horário da medição.</small>
        <small hidden={compact}>Referência regional: a chuva na estação pode diferir da chuva na propriedade. Dados não alimentam os cálculos de irrigação.</small>
        <a hidden={compact} href={`https://tempo.inmet.gov.br/tabela/mapa/${station.codigo}/${now.toISOString().slice(0, 10)}`} target="_blank" rel="noopener noreferrer">Conferir na fonte oficial INMET</a>
      </footer>
    </section>
  );
}

function InmetMobileSheet({ station, distanceKm, onClose }) {
  const [expanded, setExpanded] = useState(false);
  const closeRef = useRef(null);
  const contentId = useId();

  useEffect(() => {
    closeRef.current.focus({ preventScroll: true });
  }, []);

  return (
    <aside className={`inmet-mobile-sheet${expanded ? ' is-expanded' : ''}`} aria-label="Estação INMET selecionada"
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}>
      <div className="inmet-sheet-toolbar">
        <strong>Estação INMET</strong>
        <button type="button" aria-expanded={expanded} aria-controls={contentId} onClick={() => setExpanded(value => !value)}>
          {expanded ? 'Recolher detalhes' : 'Ver gráfico e detalhes'}
        </button>
        <button ref={closeRef} type="button" className="inmet-sheet-close" aria-label="Fechar estação INMET" onClick={onClose}>×</button>
      </div>
      <div className="inmet-sheet-content" id={contentId}>
        <InmetStationPanel station={station} distanceKm={distanceKm} compact={!expanded} />
      </div>
    </aside>
  );
}

export default function InmetStationsLayer({ onStatus, referencePoint }) {
  const map = useMap();
  const [stations, setStations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    let active = true;
    setError('');
    onStatus({ loading: true, count: 0, error: '' });
    listInmetStations().then(data => {
      if (!active) return;
      setStations(data);
      onStatus({
        loading: false, count: data.length,
        error: data.length ? '' : 'Catálogo INMET ainda não sincronizado. É necessária a primeira execução do coletor.',
      });
    }).catch(err => {
      console.error('[INMET] Erro ao carregar catálogo:', err);
      if (active) {
        setError(err.message);
        onStatus({ loading: false, count: 0, error: err.message });
      }
    });
    return () => { active = false; };
  }, [onStatus, retry]);

  const distance = selected && referencePoint
    ? map.distance([referencePoint.lat, referencePoint.lng], [selected.latitude, selected.longitude]) / 1000
    : null;

  return (
    <>
      {stations.map(station => (
        <CircleMarker
          key={station.codigo}
          center={[station.latitude, station.longitude]}
          radius={7}
          pathOptions={{ color: '#ffffff', weight: 2, fillColor: station.situacao === 'Operante' ? '#007f96' : '#9a722e', fillOpacity: 0.95 }}
          eventHandlers={{ click: event => { event.originalEvent?.stopPropagation(); setSelected(station); } }}
        >
          <Tooltip direction="top">
            <strong>INMET · {station.nome}/{station.uf}</strong><br />
            {station.codigo} · {station.situacao} · Clique para consultar
          </Tooltip>
        </CircleMarker>
      ))}
      {selected && isMobile && createPortal(
        <InmetMobileSheet
          key={selected.codigo}
          station={selected}
          distanceKm={distance}
          onClose={() => {
            setSelected(null);
            map.getContainer().focus({ preventScroll: true });
          }}
        />,
        map.getContainer().closest('.remote-sensing-page') || document.body
      )}
      {selected && !isMobile && (
        <Popup
          key={selected.codigo}
          position={[selected.latitude, selected.longitude]}
          minWidth={240}
          maxWidth={420}
          className="inmet-map-popup"
          eventHandlers={{ remove: () => {
            if (!window.matchMedia(MOBILE_QUERY).matches) {
              setSelected(current => current?.codigo === selected.codigo ? null : current);
            }
          } }}
        >
          <InmetStationPanel station={selected} distanceKm={distance} />
        </Popup>
      )}
      {error && (
        <Popup position={map.getCenter()} className="inmet-map-popup">
          <p role="alert">{error}</p>
          <button type="button" onClick={() => setRetry(value => value + 1)}>Tentar novamente</button>
        </Popup>
      )}
    </>
  );
}
